import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { User, ClockCounterClockwise } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (d) => {
  if (!d) return '—';
  const p = String(d).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

/**
 * Phase 282 — Building-wide Owner History timeline (read-only).
 * Lists every owner change across all apartments in the building, newest first.
 * Each row links into the related Apartment Details → Owner History tab.
 */
export default function BuildingOwnerHistoryTab({ buildingId }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'previous'

  useEffect(() => {
    if (!buildingId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/buildings/${buildingId}/owner-history`);
        setRows(res.data || []);
      } catch (err) {
        toast.error(t('errorOccurred'));
      } finally {
        setLoading(false);
      }
    })();
  }, [buildingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = rows.filter((r) => filter === 'all' || r.status === filter);

  return (
    <div className="space-y-4" data-testid="building-owner-history-tab">
      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ClockCounterClockwise size={18} weight="fill" className="text-zinc-600" />
            <div>
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('ownerHistoryTitle')}
              </h3>
              <p className="text-[11px] text-zinc-500">{t('buildingOwnerHistoryDesc')}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs">
            {['all', 'active', 'previous'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 border ${filter === f ? 'bg-zinc-950 text-white border-zinc-950' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}
                data-testid={`bld-owner-history-filter-${f}`}
              >
                {f === 'all' ? t('all') : f === 'active' ? t('statusActive') : t('statusPrevious')}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-400">…</div>
        ) : visible.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-400" data-testid="no-building-owner-history">
            {t('noOwnerHistory')}
          </div>
        ) : (
          <ol className="divide-y divide-zinc-100" data-testid="building-owner-history-list">
            {visible.map((row) => {
              const fullName = `${row.first_name || ''} ${row.last_name || ''}`.trim() || '—';
              const isActive = row.status === 'active';
              return (
                <li
                  key={row.owner_history_id}
                  className="px-5 py-4 flex items-start gap-4 hover:bg-zinc-50 cursor-pointer"
                  onClick={() => navigate(`/apartments/${row.apartment_id}?tab=ownerHistory`)}
                  data-testid={`bld-owner-history-row-${row.owner_history_id}`}
                >
                  <div className="flex flex-col items-center pt-1">
                    <span className={`block w-2.5 h-2.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-mono">
                        {t('apartmentLabel')} #{row.apartment_number || '—'}
                      </span>
                      <span className="font-medium text-zinc-900">{fullName}</span>
                      <span className={`text-[10px] px-2 py-0.5 ${isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-zinc-100 text-zinc-600 border border-zinc-200'}`}>
                        {isActive ? t('statusActive') : t('statusPrevious')}
                      </span>
                      {row.ownership_percentage != null && (
                        <span className="text-[10px] text-zinc-500 font-mono">{row.ownership_percentage}%</span>
                      )}
                      {row.reason && (
                        <span className="text-[10px] text-zinc-500 italic">
                          · {t('reason' + row.reason.charAt(0).toUpperCase() + row.reason.slice(1))}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                      <span className="font-mono">{formatDateDMY(row.start_date)} → {formatDateDMY(row.end_date)}</span>
                      {row.phone && <span>{row.phone}</span>}
                      {row.email && <span>{row.email}</span>}
                    </div>
                  </div>
                  <User size={16} className="text-zinc-300 mt-1" />
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
